import { i18n } from '@/plugins/i18n';
import { ResponseError, FetchError } from '@/clients/dashboardapi/v2';

export enum NotificationType {
    ERROR = 'ERROR',
    WARN = 'WARN',
    INFO = 'INFO',
    SUCCESS = 'SUCCESS',
}

export interface Notification {
    title: string;
    text: string;
    type: NotificationType;
    shouldHideAutomatically: boolean;
}

export default class NotificationService {

    public static onNotificationAdded: (notification: Notification) => void;
    public static onNotificationRemoved: (notification: Notification) => void;

    public static success(title: string, text: string, shouldHideAutomatically = true) {
        this.showNotification(title, text, shouldHideAutomatically, NotificationType.SUCCESS);
    }

    public static info(title: string, text: string, shouldHideAutomatically = true) {
        this.showNotification(title, text, shouldHideAutomatically, NotificationType.INFO);
    }

    public static warn(title: string, text: string, shouldHideAutomatically = true) {
        this.showNotification(title, text, shouldHideAutomatically, NotificationType.WARN);
    }

    public static error(title: string, message: string, shouldHideAutomatically = false) {
        console.error(title, message);
        this.showNotification(title, message, shouldHideAutomatically, NotificationType.ERROR);
    }

    public static isFetchError(e: unknown): e is FetchError {
        const hasNameProperty = typeof e === 'object' && e !== null && Object.hasOwn(e as object, 'name');

        return hasNameProperty && (e as Error).name === 'FetchError';
    }

    public static isResponseError(e: unknown): e is ResponseError {
        const hasNameProperty = typeof e === 'object' && e !== null && Object.hasOwn(e as object, 'name');

        return hasNameProperty && (e as Error).name === 'ResponseError';
    }

    public static async serviceError(error: unknown, additionalInfo?: string, shouldHideAutomatically = false) {
        let title: string;
        let message = '';

        if (this.isFetchError(error)) {
            title = `[503] ${i18n.t('error.503').toString()}`;
            message = String(error);
        } else if (!this.isResponseError(error)) {
            title = i18n.t('error.undefined').toString();
            message = String(error);
        } else {
            const key = `error.${error.response.status}`;
            if (i18n.te(key)) {
                title = `[${error.response.status}] ${i18n.t(key).toString()}`;
            } else {
                title = i18n.t('error.undefined').toString();
            }
            message = await error.response.text() ?? error.message;
        }
        if (additionalInfo) {
            title += ` - ${additionalInfo}`;
        }
        this.error(title, message, shouldHideAutomatically);
    }

    public static showNotification(title: string, text: string, shouldHideAutomatically: boolean, type: NotificationType = NotificationType.INFO) {
        const n: Notification = { title, text, type, shouldHideAutomatically };
        this.onNotificationAdded(n);
        if (shouldHideAutomatically) {
            setTimeout(() => this.hideNotification(n), 6000);
        }
    }

    public static hideNotification(n: Notification) {
        this.onNotificationRemoved(n);
    }
}
